/**
 * Appearance card container: every control writes one `ui.*` preference through `PUT /preferences`
 * (D-123: personal, no PIN, not audited). [C30] D-285: Theme Studio opens the old modal; its theme, display scale and
 * dashboard icon size are `ui.theme_studio`, `ui.display_scale`, `ui.icon_size`. The icon picker arrives in B4 (D-124).
 */
import { useEffect, useRef, useState } from "react";
import { AppearanceCard, ThemeStudioModal, applyThemeToDocument, themeForPreference } from "@faylasuf/ui";
import { useApp, useSession } from "../../app/context.js";
import { errorMessage, isSessionLost } from "../../app/errors.js";
import type { UiPrefs } from "../../app/preferences.js";
import { useToast } from "../../app/toast.js";

export function AppearanceContainer({ prefs, setPreference }: { prefs: UiPrefs; setPreference: (key: string, value: unknown, onError?: (e: unknown) => void) => void }) {
  const { t } = useApp();
  const { sessionLost } = useSession();
  const toast = useToast();
  const onError = (e: unknown) => (isSessionLost(e) ? sessionLost() : toast(errorMessage(t, e), "error"));
  const set = (key: string, value: unknown) => setPreference(key, value, onError);
  const comingB4 = () => toast(t("app.coming_b4"), "info");
  const [studio, setStudio] = useState(false);
  // [C30 review] the slider fires on every step: the zoom follows at once, the preference is written once it rests
  const scaleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (scaleTimer.current) clearTimeout(scaleTimer.current); }, []);
  const [scale, setScaleState] = useState(prefs.displayScale);
  useEffect(() => setScaleState(prefs.displayScale), [prefs.displayScale]);
  const setScale = (v: number) => {
    setScaleState(v);
    (document.documentElement.style as CSSStyleDeclaration & { zoom: string }).zoom = v === 100 ? "" : `${v}%`;
    if (scaleTimer.current) clearTimeout(scaleTimer.current);
    scaleTimer.current = setTimeout(() => set("ui.display_scale", v), 400);
  };

  return (
    <>
    <AppearanceCard
      {...(prefs.icons["layout"] ? { customIcon: prefs.icons["layout"]! } : {})}
      darkMode={prefs.theme === "dark"}
      onDarkModeChange={(dark) => set("ui.theme", dark ? "dark" : "light")}
      onThemeStudio={() => setStudio(true)}
      themeColor={prefs.accent}
      onThemeColorChange={(c) => set("ui.accent", c)}
      cardSize={prefs.cardSize}
      onCardSizeChange={(s) => set("ui.card_size", s)}
      onCustomizeIcons={comingB4}
      language={prefs.language}
      onLanguageChange={(l) => set("ui.language", l)}
    />
    {/* mounted only while open, so its selection starts from the stored theme every time (C30 review) */}
    {studio && <ThemeStudioModal
      isOpen={studio}
      onClose={() => setStudio(false)}
      activeTheme={prefs.themeStudio}
      setActiveTheme={(theme) => { applyThemeToDocument(theme); set("ui.theme_studio", themeForPreference(theme)); }}
      darkMode={prefs.theme === "dark"}
      setDarkMode={(dark) => set("ui.theme", dark ? "dark" : "light")}
      lang={prefs.language}
      displayResolution={scale}
      setDisplayResolution={setScale}
      dashboardIconSize={prefs.iconSize}
      setDashboardIconSize={(v) => set("ui.icon_size", v)}
    />}
    </>
  );
}
